import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import Icon from './Icon';

interface Option {
  value: any;
  label: string;
  Icon: React.ReactNode;
}

interface SelectOptionProps {
  option: Option;
  index: number;
  selected: any;
  onSelect: (value: string) => void;
}

const SelectOption = React.memo(({ option, index, selected, onSelect }: SelectOptionProps) => {
  const uniqueKey = index;

  return (
    <div
      key={uniqueKey}
      role="menuitem"
      tabIndex={index}
      className="flex w-full cursor-default select-none items-center justify-between rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100"
      onClick={() => onSelect(option.value)}
    >
      <div className={`flex items-center gap-3`}>
        {option.Icon}
        <span className="grow">{option.label}</span>
      </div>
      <span className="mr-2 flex size-4 items-center justify-center">
        {selected === option.value ? <Icon name="IconCheck" className="size-4" /> : null}
      </span>
    </div>
  );
});

SelectOption.displayName = 'SelectOption';

interface SelectDropdownProps {
  options: Option[];
  selected: any;
  onChange: (selected: any) => void;
  className?: string;
  disabled?: boolean;
}

const SelectDropdownV2: React.FC<SelectDropdownProps> = ({
  options,
  selected,
  onChange,
  className = '',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent): void => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mouseup', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  const handleSelect = useCallback(
    (option: string): void => {
      onChange(option);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!disabled) {
        setIsOpen(!isOpen);
      }
    },
    [disabled, isOpen]
  );

  const selectedOption = options.find(option => option.value === selected);

  const SelectedIcon = selectedOption?.Icon ?? null;

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <button
        data-testid="select-dropdown-button"
        className={`inline-flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg border bg-white px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-theme-input-focus ${
          disabled ? 'pointer-events-none opacity-50' : ''
        }`}
        onClick={handleButtonClick}
      >
        <div className="flex items-center gap-2">
          {SelectedIcon}
          <span className="block truncate">{selectedOption?.label ?? 'Select...'}</span>
        </div>
        <Icon name="IconChevronDown" className="size-4 opacity-50" />
      </button>
      {isOpen && !disabled && (
        <div
          className={`absolute left-0 top-full z-[9999] mt-1 w-full min-w-[160px] rounded-md border bg-white p-1 shadow-md transition-all`}
        >
          <>
            {options && options.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-gray-500">No options available</div>
            ) : (
              options?.map((option, index) => (
                <SelectOption key={index} option={option} index={index} selected={selected} onSelect={handleSelect} />
              ))
            )}
          </>
        </div>
      )}
    </div>
  );
};

export default memo(SelectDropdownV2);
